import React, { FC, ReactNode, useEffect, useMemo, useRef } from 'react';

import { classNames } from '../../utils/style';
import { PayFitColor } from '../../types/module';
import { scrollAnimate } from '../../utils/scrollAnimate';
import { container } from '../../styles/themes';
import {
  textBodyUppercase,
  textTitle1,
  textTitle3,
} from '../../styles/typography';
import { Picto, PictoProps } from '../../components/Picto';
import { Image } from '../../components/Image';
import { applyBgColor } from '../../utils/utils';

import {
  applyColumnSize,
  applyContainerSize,
  applyContentSize,
  applyValuePropSize,
} from './logic';
import { ImageProps } from '../../components/Image/types';

export interface ValuePropositionColumnsItemsProps {
  title?: ReactNode;
  paragraph?: ReactNode;
  picto?: PictoProps;
  cta?: ReactNode;
}

export interface ValuePropositionProps {
  surtitle?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  columns: number;
  columnsItems?: ValuePropositionColumnsItemsProps[];
  contentText?: {
    title: ReactNode;
    paragraph: ReactNode;
  };
  contentImage?: ImageProps;
  contentCta?: ReactNode;
  contentRight?: boolean;
  isDark?: boolean;
  className?: string;
  iconBackground?: boolean;
  backgroundColor?: PayFitColor;
  id?: string;
  disabledAnimation?: boolean;
  forceBgCodeColor?: 21 | 51 | 101 | 301;
}

export const ValueProposition: FC<ValuePropositionProps> = ({
  surtitle,
  title,
  subtitle,
  columns,
  contentText,
  contentImage,
  contentCta,
  columnsItems,
  contentRight,
  isDark = false,
  backgroundColor = 'white',
  className = '',
  id = '',
  disabledAnimation = true,
  forceBgCodeColor,
}) => {
  const refTitle = useRef(null);
  const noContent = columns > 2 || (!contentText && !contentImage);
  const hasContentRight = noContent ? false : contentRight;

  useEffect(() => {
    scrollAnimate(refTitle.current);
  }, [refTitle]);

  const iconVariant = useMemo(() => {
    if (isDark && backgroundColor === 'blue') return 'light';
    if (backgroundColor === 'white') return 'light';

    return 'dark';
  }, [backgroundColor, isDark]);

  return (
    <div
      className={classNames(
        'ValueProposition',
        applyBgColor(
          backgroundColor,
          isDark,
          isDark ? 'bg-blue-301' : 'bg-blue-21',
          forceBgCodeColor,
        ),
        className,
      )}
      id={id}
    >
      <div
        ref={refTitle}
        className={classNames(
          container,
          'flex flex-col items-center',
          !disabledAnimation && 'slideInUp-30 wait',
        )}
      >
        {(title || subtitle || surtitle) && (
          <div
            className={classNames(
              'max-w-[624px] flex flex-col item-start md:items-center',
              'pb-48 text-left md:text-center lg:pb-64',
            )}
          >
            {surtitle && (
              <p
                className={classNames(
                  textBodyUppercase,
                  'mb-24',
                  isDark ? 'text-neutral-1' : 'text-neutral-101',
                )}
              >
                {surtitle}
              </p>
            )}
            {title && (
              <h2
                className={classNames(
                  textTitle1,
                  isDark ? 'text-neutral-1' : 'text-neutral-301',
                )}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <div
                className={classNames(
                  isDark ? 'text-neutral-1' : 'text-neutral-101',
                )}
              >
                {subtitle}
              </div>
            )}
          </div>
        )}
        <div
          className={classNames(
            hasContentRight ? 'flex-row-reverse' : '',
            applyContainerSize(columns, noContent),
            !noContent ? 'md:gap-24 lg:gap-64' : '',
            'flex flex-wrap md:flex-nowrap',
          )}
        >
          {(contentText?.title ||
            contentText?.paragraph ||
            contentCta ||
            contentImage) && (
            <div
              className={classNames(
                applyContentSize(columns, noContent),
                'mb-32',
                isDark ? 'text-neutral-1' : 'text-neutral-101',
              )}
            >
              {contentText?.title &&
                (!title ? (
                  <h2
                    className={classNames(
                      isDark ? 'text-neutral-1' : 'text-neutral-301',
                      'font-medium pb-24',
                      textTitle1,
                    )}
                  >
                    {contentText.title}
                  </h2>
                ) : (
                  <p
                    className={classNames(
                      isDark ? 'text-neutral-1' : 'text-neutral-301',
                      'font-medium pb-24',
                      textTitle1,
                    )}
                  >
                    {contentText?.title}
                  </p>
                ))}
              {contentText?.paragraph && contentText.paragraph}
              {contentImage && (
                <div
                  className="w-full relative overflow-hidden rounded-18"
                  style={{
                    height: columns === 1 ? 362 : 264,
                  }}
                >
                  <Image
                    {...contentImage}
                    className="w-full h-full object-cover"
                    sizes={[
                      '(max-width: 350px) 318px',
                      '(max-width: 480px) 450px',
                      '(max-width: 768px) 719px',
                      '(max-width: 1024px) 344px',
                      '468px',
                    ]}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(transparent, rgba(0, 0, 0, 0.13), rgba(0, 0, 0, 0.13))`,
                    }}
                  />
                </div>
              )}
              {contentCta && <div className="mt-24">{contentCta}</div>}
            </div>
          )}
          <div className={`${applyColumnSize(columns, noContent)}`}>
            <div
              className={`flex gap-y-48 justify-center  ${
                columns !== 1 ? 'flex-wrap' : 'flex-col'
              }`}
            >
              {columnsItems?.map((columnItem, columnKey) => {
                return (
                  <div
                    className={classNames(
                      columns === 5 ? 'flex-col items-start' : '',
                      applyValuePropSize(columns, noContent),
                      'flex',
                      isDark ? 'text-neutral-1' : 'text-neutral-101',
                    )}
                    key={columnKey}
                  >
                    {columnItem?.picto && (
                      <div className="flex justify-center">
                        <Picto
                          name={columnItem.picto.name}
                          className={classNames('mb-16 mr-16 w-48 h-48')}
                          variant={iconVariant}
                        />
                      </div>
                    )}
                    <div className={`text-left`}>
                      {columnItem?.title && (
                        <p
                          className={classNames(
                            isDark ? 'text-neutral-21' : 'text-neutral-301',
                            'font-medium mb-8 ',
                            textTitle3,
                          )}
                        >
                          {columnItem.title}
                        </p>
                      )}
                      {columnItem?.paragraph && columnItem.paragraph}
                      {columnItem?.cta && (
                        <div className="mt-48">{columnItem.cta}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
